'use client'

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { generateTrial, type TrialResult, type Verdict } from '@/lib/courtroom'

// ============================================================================
// THE COURTROOM — enter something you did, and stand trial for it.
// ============================================================================

type Phase = 'idle' | 'session' | 'verdict'

const EXAMPLES = [
  "I ate my friend's fries.",
  'I left three texts on read.',
  'I hit snooze nine times.',
  'I bought something I did not need.',
  "I skipped the gym... again.",
  'I told them traffic was bad. It wasn’t.',
]

// Theme toggle (kept consistent with the rest of the app).
function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const saved = localStorage.getItem('theme') as 'light' | 'dark' | null
    const initial = saved || 'dark'
    setTheme(initial)
    document.documentElement.classList.toggle('dark', initial === 'dark')
  }, [])

  const toggle = () => {
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    document.documentElement.classList.toggle('dark', next === 'dark')
    localStorage.setItem('theme', next)
  }

  if (!mounted) return <div className="w-10 h-10" />

  return (
    <button
      onClick={toggle}
      className="w-10 h-10 rounded-full bg-amber-100/10 border border-amber-200/20 flex items-center justify-center text-amber-200/80 hover:bg-amber-100/20 transition-colors"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
        </svg>
      )}
    </button>
  )
}

// Gavel mark used as the site emblem.
function Gavel({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 4.5l6 6" />
      <path d="M9.5 8.5l2-2a2.5 2.5 0 013.5 0l1.5 1.5a2.5 2.5 0 010 3.5l-2 2a2.5 2.5 0 01-3.5 0L9.5 12a2.5 2.5 0 010-3.5z" />
      <path d="M11 13l-6.5 6.5a1.6 1.6 0 01-2.3-2.3L8.7 10.7" />
      <path d="M5 21h8" />
    </svg>
  )
}

const verdictStyles: Record<Verdict, { text: string; glow: string; badge: string }> = {
  GUILTY: {
    text: 'text-red-400',
    glow: 'shadow-[0_0_60px_-10px_rgba(248,113,113,0.5)]',
    badge: 'border-red-400/40 bg-red-500/10',
  },
  'NOT GUILTY': {
    text: 'text-emerald-400',
    glow: 'shadow-[0_0_60px_-10px_rgba(52,211,153,0.5)]',
    badge: 'border-emerald-400/40 bg-emerald-500/10',
  },
  MISTRIAL: {
    text: 'text-amber-300',
    glow: 'shadow-[0_0_60px_-10px_rgba(252,211,77,0.5)]',
    badge: 'border-amber-300/40 bg-amber-400/10',
  },
}

// A single argument card (prosecution / defense).
function ArgumentBlock({
  side,
  accent,
  lines,
  delay,
}: {
  side: string
  accent: string
  lines: string[]
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl border border-amber-100/10 bg-neutral-900/40 backdrop-blur-sm p-6 sm:p-7"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-semibold tracking-[0.25em] uppercase ${accent}`}>
          {side}
        </span>
        <div className="h-px flex-1 bg-gradient-to-r from-amber-100/20 to-transparent" />
      </div>
      <div className="space-y-3">
        {lines.map((line, i) => (
          <motion.p
            key={line}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.25 + i * 0.25 }}
            className="text-[15px] leading-relaxed text-neutral-200"
          >
            <span className={`mr-1 ${accent}`}>&ldquo;</span>
            {line}
            <span className={`ml-0.5 ${accent}`}>&rdquo;</span>
          </motion.p>
        ))}
      </div>
    </motion.div>
  )
}

export default function CourtroomPage() {
  const [mounted, setMounted] = useState(false)
  const [confession, setConfession] = useState('')
  const [lastConfession, setLastConfession] = useState('')
  const [phase, setPhase] = useState<Phase>('idle')
  const [trial, setTrial] = useState<TrialResult | null>(null)
  const [placeholder, setPlaceholder] = useState(EXAMPLES[0])

  useEffect(() => {
    setMounted(true)
    document.documentElement.classList.add('dark')
    setPlaceholder(EXAMPLES[Math.floor(Math.random() * EXAMPLES.length)])
  }, [])

  const runTrial = useCallback((text: string) => {
    const t = text.trim()
    if (!t) return
    setLastConfession(t)
    setTrial(generateTrial(t))
    setPhase('session')
    // Let arguments read for a beat, then drop the verdict.
    window.clearTimeout((runTrial as unknown as { _t?: number })._t)
    ;(runTrial as unknown as { _t?: number })._t = window.setTimeout(() => {
      setPhase('verdict')
    }, 2600)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    runTrial(confession)
  }

  const retry = () => {
    setTrial(generateTrial(lastConfession))
    setPhase('session')
    window.setTimeout(() => setPhase('verdict'), 2600)
  }

  const reset = () => {
    setPhase('idle')
    setTrial(null)
    setConfession('')
    setPlaceholder(EXAMPLES[Math.floor(Math.random() * EXAMPLES.length)])
  }

  if (!mounted) return <div className="min-h-screen bg-neutral-950" />

  const vStyle = trial ? verdictStyles[trial.verdict] : verdictStyles.GUILTY

  return (
    <div className="dark min-h-screen bg-neutral-950 text-neutral-100 relative overflow-hidden">
      {/* Ambient courtroom lighting */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[900px] rounded-full bg-amber-500/10 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '42px 42px',
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={reset} className="flex items-center gap-2.5 group">
            <span className="text-amber-300 group-hover:rotate-[-12deg] transition-transform">
              <Gavel className="w-6 h-6" />
            </span>
            <span className="font-serif text-lg tracking-wide text-amber-50">The Courtroom</span>
          </button>
          <ThemeToggle />
        </div>
      </header>

      <main className="relative z-10 px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            {/* ---------------------------------------------------------------- */}
            {/* IDLE — the confession booth                                       */}
            {/* ---------------------------------------------------------------- */}
            {phase === 'idle' && (
              <motion.section
                key="idle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="pt-16 sm:pt-24 text-center"
              >
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-3xl border border-amber-200/20 bg-amber-500/5 text-amber-300 mb-8"
                >
                  <Gavel className="w-10 h-10" />
                </motion.div>

                <h1 className="font-serif text-4xl sm:text-6xl tracking-tight text-amber-50 mb-5">
                  Confess. Stand trial.
                </h1>
                <p className="text-neutral-400 text-lg max-w-xl mx-auto mb-12">
                  Tell the court something you did. The prosecution and defense will
                  argue it out — then the gavel falls.
                </p>

                <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
                  <div className="relative">
                    <textarea
                      value={confession}
                      onChange={(e) => setConfession(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleSubmit(e as unknown as React.FormEvent)
                        }
                      }}
                      rows={3}
                      placeholder={placeholder}
                      autoFocus
                      className="w-full resize-none rounded-2xl border border-amber-100/15 bg-neutral-900/60 px-5 py-4 text-lg text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-amber-300/50 focus:ring-1 focus:ring-amber-300/30 transition-colors"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileTap={{ scale: 0.97 }}
                    disabled={!confession.trim()}
                    className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-amber-400 text-neutral-950 font-semibold hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Gavel className="w-5 h-5" />
                    Bring me to trial
                  </motion.button>
                </form>

                <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
                  {EXAMPLES.slice(0, 4).map((ex) => (
                    <button
                      key={ex}
                      onClick={() => runTrial(ex)}
                      className="text-sm px-3 py-1.5 rounded-full border border-amber-100/10 text-neutral-400 hover:text-amber-100 hover:border-amber-200/30 transition-colors"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </motion.section>
            )}

            {/* ---------------------------------------------------------------- */}
            {/* SESSION + VERDICT — the trial itself                             */}
            {/* ---------------------------------------------------------------- */}
            {(phase === 'session' || phase === 'verdict') && trial && (
              <motion.section
                key="trial"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="pt-10 sm:pt-14"
              >
                {/* The charge sheet */}
                <motion.div
                  initial={{ opacity: 0, y: -16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-10"
                >
                  <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-300/80 mb-3">
                    Court is now in session
                  </p>
                  <p className="text-neutral-400 text-sm mb-2">The defendant stands accused of</p>
                  <h2 className="font-serif text-2xl sm:text-3xl text-amber-50 mb-4">
                    {trial.charge}
                  </h2>
                  <p className="max-w-xl mx-auto italic text-neutral-300">
                    &ldquo;{lastConfession}&rdquo;
                  </p>
                </motion.div>

                {/* Arguments */}
                <div className="grid gap-4 sm:gap-5">
                  <ArgumentBlock
                    side="Prosecution"
                    accent="text-red-400"
                    lines={trial.prosecution}
                    delay={0.5}
                  />
                  <ArgumentBlock
                    side="Defense"
                    accent="text-emerald-400"
                    lines={trial.defense}
                    delay={1.3}
                  />
                </div>

                {/* Deliberation shimmer before the verdict */}
                <AnimatePresence>
                  {phase === 'session' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="mt-8 flex items-center justify-center gap-3 text-neutral-400"
                    >
                      <span className="text-sm tracking-wide">The jury is deliberating</span>
                      <span className="flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.span
                            key={i}
                            className="w-1.5 h-1.5 rounded-full bg-amber-300"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                          />
                        ))}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* The verdict */}
                <AnimatePresence>
                  {phase === 'verdict' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 220, damping: 18 }}
                      className={`mt-8 rounded-2xl border ${vStyle.badge} ${vStyle.glow} p-8 text-center`}
                    >
                      <motion.div
                        initial={{ rotate: -30, y: -10, opacity: 0 }}
                        animate={{ rotate: 0, y: 0, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 12, delay: 0.05 }}
                        className={`inline-flex mb-3 ${vStyle.text}`}
                      >
                        <Gavel className="w-8 h-8" />
                      </motion.div>
                      <p className="text-xs font-semibold tracking-[0.3em] uppercase text-neutral-400 mb-2">
                        Verdict
                      </p>
                      <h3 className={`font-serif text-5xl sm:text-6xl font-bold tracking-tight ${vStyle.text} mb-5`}>
                        {trial.verdict}
                      </h3>
                      <div className="max-w-md mx-auto">
                        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500 mb-2">
                          Sentence
                        </p>
                        <p className="text-lg text-neutral-100">{trial.sentence}</p>
                      </div>

                      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                          onClick={retry}
                          className="w-full sm:w-auto px-6 py-3 rounded-xl border border-amber-200/25 text-amber-100 font-medium hover:bg-amber-100/10 transition-colors"
                        >
                          Appeal (retry)
                        </button>
                        <button
                          onClick={reset}
                          className="w-full sm:w-auto px-6 py-3 rounded-xl bg-amber-400 text-neutral-950 font-semibold hover:bg-amber-300 transition-colors"
                        >
                          New confession
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-6 text-center">
        <p className="text-sm text-neutral-600">
          The Courtroom · verdicts are legally meaningless and morally questionable
        </p>
      </footer>
    </div>
  )
}
